import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton, PageHead } from "@/v2/admin/ui";
import { Settings, BrainCircuit, Save, ShieldCheck } from "lucide-react";

export default function AISettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-ai-settings"], queryFn: async () => (await api.get("/admin/ai-settings")).data });
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const s = f || data;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/ai-settings", {
        strictness: s.strictness, feedback_style: s.feedback_style,
        pass_score: Number(s.pass_score), min_answer_length: Number(s.min_answer_length),
        criteria: (s.criteria || []).map((c) => ({ name: c.name, weight: Number(c.weight) })),
      });
      toast.success("تم حفظ إعدادات التقييم الذكي");
      setF(null);
      qc.invalidateQueries({ queryKey: ["adm-ai-settings"] });
    } catch { toast.error("تعذّر الحفظ"); } finally { setSaving(false); }
  };

  if (!s) return <Skeleton className="h-72 w-full" />;
  const total = (s.criteria || []).reduce((a, c) => a + Number(c.weight || 0), 0);

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-settings">
      <PageHead icon={Settings} title="إعدادات التقييم الذكي" sub="تحكم بمعايير وأسلوب تصحيح الذكاء الاصطناعي للإجابات النصية." />

      <GlassCard gold className="p-5 sm:p-6 space-y-5">
        <div className="flex items-center gap-3 v2-inset p-4">
          <div className="w-11 h-11 rounded-2xl bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center"><BrainCircuit size={22} /></div>
          <div className="flex-1">
            <div className="font-bold text-sm">نموذج التقييم النشط</div>
            <div className="text-xs text-[#bcc8cf]">{s.model_provider} / {s.model_name} — عبر مفتاح آمن في الخادم</div>
          </div>
          <Chip tone="emerald"><ShieldCheck size={12} /> المفتاح محمي</Chip>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="v2-label">مستوى التشدد في التقييم</label>
            <select value={s.strictness} onChange={(e) => setF({ ...s, strictness: e.target.value })} data-testid="ai-strictness" className="v2-input">
              {["متساهل", "متوازن", "متشدد"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="v2-label">أسلوب الملاحظات (Feedback)</label>
            <select value={s.feedback_style} onChange={(e) => setF({ ...s, feedback_style: e.target.value })} data-testid="ai-style" className="v2-input">
              {["تشجيعي", "مباشر", "تفصيلي"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="v2-label">درجة النجاح الافتراضية %</label>
            <input type="number" value={s.pass_score} onChange={(e) => setF({ ...s, pass_score: e.target.value })} data-testid="ai-pass" className="v2-input" />
          </div>
          <div>
            <label className="v2-label">الحد الأدنى لطول الإجابة (أحرف)</label>
            <input type="number" value={s.min_answer_length} onChange={(e) => setF({ ...s, min_answer_length: e.target.value })} data-testid="ai-minlen" className="v2-input" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="v2-label !mb-0">معايير التقييم وأوزانها</label>
            <Chip tone={total === 100 ? "emerald" : "gold"}>المجموع: {total}%</Chip>
          </div>
          <div className="space-y-2">
            {(s.criteria || []).map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <input value={c.name} onChange={(e) => { const cr = [...s.criteria]; cr[i] = { ...cr[i], name: e.target.value }; setF({ ...s, criteria: cr }); }}
                  data-testid={`ai-crit-name-${i}`} className="v2-input flex-1" />
                <input type="number" value={c.weight} onChange={(e) => { const cr = [...s.criteria]; cr[i] = { ...cr[i], weight: e.target.value }; setF({ ...s, criteria: cr }); }}
                  data-testid={`ai-crit-weight-${i}`} className="v2-input !w-24 text-center" />
                <span className="text-xs text-[#bcc8cf]">%</span>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving || !f} data-testid="ai-save" className="v2-btn w-full !h-12"><Save size={16} /> {saving ? "جارٍ الحفظ..." : "حفظ الإعدادات"}</button>
      </GlassCard>
    </div>
  );
}
